/**
 * La SELECCIÓN del inspector: qué episodio está abierto y qué respuesta del
 * asesor se está inspeccionando. Viaja en la URL (`?ep=&turno=`) para poder
 * enlazar un mensaje concreto; lo que la URL pida y no exista cae en el
 * defecto (el episodio más reciente, su última respuesta) — un enlace viejo
 * no deja la pantalla en blanco.
 */
import { recallWithoutFragment, type EpisodeSummary, type PuenteEpisodio, type Turn, type TurnTrace } from "@skein/contracts";

export interface ImplantacionSearch {
  /** conmutador dev de estados (cargando/error), ya existente */
  estado?: string;
  /** conversationId del episodio abierto */
  ep?: string;
  /** índice del turno inspeccionado DENTRO del episodio (orden de la API) */
  turno?: number;
}

/** `validateSearch` de la ruta: lo que no sea legible, no entra. */
export function leerSearch(raw: Record<string, unknown>): ImplantacionSearch {
  const out: ImplantacionSearch = {};
  for (const k of ["estado", "ep"] as const) {
    const v = raw[k];
    if (typeof v === "string" && v !== "") out[k] = v;
  }
  const t = raw["turno"];
  const n = typeof t === "number" ? t : typeof t === "string" && t.trim() !== "" ? Number(t) : Number.NaN;
  if (Number.isInteger(n) && n >= 0) out.turno = n;
  return out;
}

/** Del más reciente al más viejo: el orden del feed y el del defecto son el mismo. */
export const porRecencia = (episodios: readonly EpisodeSummary[]): EpisodeSummary[] =>
  [...episodios].sort((a, b) => (a.hasta < b.hasta ? 1 : -1));

/** El episodio abierto: el que pida la URL; si no lo pide o ya no está, el más reciente. */
export function episodioAbierto(episodios: readonly EpisodeSummary[], ep?: string | undefined): EpisodeSummary | undefined {
  const orden = porRecencia(episodios);
  return (ep !== undefined ? orden.find((e) => e.conversationId === ep) : undefined) ?? orden[0];
}

/**
 * De qué episodios se piden los turnos. El feed CITA el arranque de las
 * conversaciones recientes; más allá del tope serían N peticiones (la
 * conversación entera) por una línea de texto, así que los viejos se
 * quedan con su fecha y su recuento — decir menos, no inventar más. El
 * episodio abierto entra SIEMPRE, esté donde esté: es el del inspector.
 */
export const TOPE_TITULARES = 12;
export function episodiosAPedir(episodios: readonly EpisodeSummary[], abierto?: EpisodeSummary | undefined, tope = TOPE_TITULARES): EpisodeSummary[] {
  const orden = porRecencia(episodios);
  const pedidos = orden.slice(0, tope);
  return abierto && !pedidos.some((e) => e.conversationId === abierto.conversationId) ? [...pedidos, abierto] : pedidos;
}

/** Los turnos INSPECCIONABLES son los del asesor: la traza es de la respuesta.
 *  Quien no es el directivo se pinta como asesor, así que aquí se mide igual. */
export const turnosDelAsesor = (turnos: readonly Turn[]): number[] =>
  turnos.flatMap((t, i) => (t.role === "user" ? [] : [i]));

/** El turno abierto: el que pida la URL si señala una respuesta del asesor;
 *  si no, la última respuesta. Sin respuestas, ninguno. */
export function turnoAbierto(turnos: readonly Turn[], turno?: number | undefined): number | undefined {
  const asesor = turnosDelAsesor(turnos);
  return (turno !== undefined && asesor.includes(turno) ? turno : undefined) ?? asesor[asesor.length - 1];
}

/**
 * El titular del episodio: el ARRANQUE del primer mensaje del directivo.
 * La API no guarda título de episodio y no se inventa uno — se cita lo que
 * se dijo (instrumento de fase dev, §5 ya avisado en el banner). Corte por
 * palabra, elipsis solo si sobra texto.
 */
export function titularEpisodio(turnos: readonly Turn[] | undefined, max = 60): string | null {
  const primero = turnos?.find((t) => t.role === "user")?.content.replace(/\s+/g, " ").trim();
  if (!primero) return null;
  return primero.length <= max ? primero : `${primero.slice(0, max).replace(/\s+\S*$/, "")}…`;
}

/** «6 turnos» · «1 turno»: la casa conjuga (como «1 directivo»). */
export const cuentaTurnos = (n: number): string => `${n} ${n === 1 ? "turno" : "turnos"}`;

// ── Lo que costó (ADR-18 pt 7, revisión 2026-10-19) ──────────────────

/** Lo que el episodio lleva gastado: la suma de las respuestas que traen coste, y cuántas no lo traen. */
export interface CosteEpisodio {
  amount: number;
  currency: string;
  /** Respuestas del asesor valoradas. */
  valoradas: number;
  /** Respuestas del asesor sin consumo guardado o sin precio: anteriores a la traza, del modelo falso, o de un modelo sin precio. */
  sinValorar: number;
}

/**
 * La suma de lo que cada respuesta del asesor costó, en la moneda que
 * diga la API. Si ninguna respuesta trae coste, nada: un cero sería
 * mentir. Las monedas no se mezclan: si dos respuestas vinieran en
 * monedas distintas (no pasa: el precio es uno por servidor), manda la
 * primera y el resto cuenta como sin valorar.
 */
export function costeEpisodio(turnos: readonly Turn[] | undefined): CosteEpisodio | null {
  if (!turnos) return null;
  let amount = 0;
  let currency: string | undefined;
  let valoradas = 0;
  let sinValorar = 0;
  for (const t of turnos) {
    if (t.role === "user") continue;
    const cost = t.trace?.model?.cost ?? null;
    if (!cost || (currency !== undefined && currency !== cost.currency)) {
      sinValorar += 1;
      continue;
    }
    currency = cost.currency;
    amount += cost.amount;
    valoradas += 1;
  }
  return currency === undefined ? null : { amount, currency, valoradas, sinValorar };
}

/** «0,0312 €» · «1,24 US$»: con cuatro decimales por debajo del euro (un turno vale céntimos) y dos por encima. */
export function formatCoste(amount: number, currency: string): string {
  const digits = Math.abs(amount) < 1 ? 4 : 2;
  try {
    return new Intl.NumberFormat("es-ES", { style: "currency", currency, minimumFractionDigits: digits, maximumFractionDigits: digits }).format(amount);
  } catch {
    return `${amount.toFixed(digits)} ${currency}`;
  }
}

/** «12.400» — tokens con separador de miles de la casa. */
export const formatTokens = (n: number): string => new Intl.NumberFormat("es-ES").format(n);

// ── El episodio y su puente (ADR-21; M5 paso 3b, 2026-11-03) ──────────

/** Abierto, cerrado o anterior a los episodios con puente (sin fila propia). */
export type EstadoEpisodio = "abierto" | "cerrado" | "legado";

/** El estado como lo dice la API; una API anterior a ADR-21 no dice nada, y entonces nada se pinta. */
export function estadoEpisodio(e: EpisodeSummary): EstadoEpisodio | null {
  if (e.legado === true) return "legado";
  if (e.estado === "open") return "abierto";
  if (e.estado === "closed") return "cerrado";
  return null;
}

/** Cómo se lee el estado en la lista y en el inspector. */
export const ROTULO_ESTADO: Record<EstadoEpisodio, string> = {
  abierto: "abierto",
  cerrado: "cerrado",
  legado: "anterior a los episodios con puente",
};

/**
 * Lo que consumió escribir el puente, en tokens, como el inspector enseña
 * el de cada respuesta. Sin precio: el coste del inspector sale de la traza
 * de un turno y el puente no la tiene — antes que un precio a ojo, ninguno.
 * Sin tokens guardados (modelo falso), null.
 */
export function tokensPuente(p: PuenteEpisodio): string | null {
  const partes = [
    ...(p.tokensEntrada !== null ? [`${formatTokens(p.tokensEntrada)} de entrada`] : []),
    ...(p.tokensSalida !== null ? [`${formatTokens(p.tokensSalida)} de salida`] : []),
  ];
  return partes.length === 0 ? null : partes.join(" · ");
}

/**
 * El puente que viajó a una respuesta (`trace.bridge`): su fecha y, si el
 * episodio de donde viene está en la lista de la casa, cuál es (para
 * abrirlo). Sin puente en la traza, nada.
 */
export function puenteDeTraza(trace: TurnTrace | null | undefined, episodios: readonly EpisodeSummary[]): { fecha: string; episodio: EpisodeSummary | undefined } | null {
  const b = trace?.bridge;
  if (!b) return null;
  return { fecha: b.date, episodio: episodios.find((e) => e.conversationId === b.conversationId) };
}

// ── El recuerdo bajo demanda (ADR-21 pt 5; M5 paso 4, 2026-11-03) ─────

/** Quién dijo un fragmento recordado, como se lee en el inspector. */
export const ROTULO_AUTOR: Record<"executive" | "advisor" | "bridge", string> = {
  executive: "lo dijo su directivo/a",
  advisor: "lo dijo el asesor",
  bridge: "del puente",
};

/** Lo que la fila «recuerdo» enseña de una respuesta: qué pasó y, si se buscó, la consulta, el rango y los fragmentos con su episodio. */
export type RecuerdoDeTraza =
  | { estado: "no-pedido" }
  | { estado: "fallo"; buscado: boolean }
  | {
      estado: "encontrado" | "vacio";
      consulta: string | null;
      desde: string | null;
      hasta: string | null;
      fragmentos: Array<{ fecha: string; autor: string; episodio: EpisodeSummary | undefined; conversationId: string }>;
    };

/**
 * El recuerdo de una respuesta (`trace.recall`), si lo hubo: «no pedido»
 * (el paso corto dijo que no pedía recordar), «fallo» (la llamada o la
 * búsqueda), «vacío» (se pidió y no se encontró: el contador de OQ-20) o
 * «encontrado», con cada fragmento y, si su episodio está en la lista de
 * la casa, cuál es (para abrirlo). Sin `recall` en la traza, nada.
 */
export function recuerdoDeTraza(trace: TurnTrace | null | undefined, episodios: readonly EpisodeSummary[]): RecuerdoDeTraza | null {
  const r = trace?.recall;
  if (!r) return null;
  if (r.error === true) return { estado: "fallo", buscado: r.asked };
  if (!r.asked) return { estado: "no-pedido" };
  const fragmentos = (r.fragments ?? []).map((f) => ({
    fecha: f.date,
    autor: ROTULO_AUTOR[f.author],
    conversationId: f.conversationId,
    episodio: episodios.find((e) => e.conversationId === f.conversationId),
  }));
  return { estado: fragmentos.length ? "encontrado" : "vacio", consulta: r.query?.trim() || null, desde: r.from ?? null, hasta: r.to ?? null, fragmentos };
}

/**
 * Cuántas veces pidió recordar el directivo en el episodio y cuántas sin
 * fragmento (el contador de OQ-20, «recuerdo pedido sin fragmento»). Sin
 * ninguna petición, nada: un cero no dice nada que merezca una línea.
 */
export function recuerdosEpisodio(turnos: readonly Turn[] | undefined): { pedidos: number; sinFragmento: number } | null {
  if (!turnos) return null;
  let pedidos = 0;
  let sinFragmento = 0;
  for (const t of turnos) {
    if (t.role === "user" || !t.trace?.recall?.asked) continue;
    pedidos += 1;
    if (recallWithoutFragment(t.trace)) sinFragmento += 1;
  }
  return pedidos === 0 ? null : { pedidos, sinFragmento };
}

// ── La entrada por documento (ADR-21 pt 6; M5 paso 5, 2026-11-03) ─────

/** Lo que la fila «documento» enseña de una respuesta: el nombre, cuántos dejó, cuántos corrigen y lo que la red dejó fuera. */
export type DocumentoDeTraza =
  | { estado: "fallo"; nombre: string }
  | { estado: "leido"; nombre: string; candidatos: number; correcciones: number; fuera: string | null };

/**
 * La entrada por documento de una respuesta (`trace.document`), si la hubo:
 * «fallo» (la llamada que lee el documento falló o se pasó de tiempo: no se
 * propuso nada) o «leído», con cuántos hechos dejó por confirmar, cuántos
 * corrigen uno confirmado y, si la red del dominio dejó algo fuera, por qué
 * (repetidos, cifras, demasiado largos, más allá del tope). Nunca el texto.
 */
export function documentoDeTraza(trace: TurnTrace | null | undefined): DocumentoDeTraza | null {
  const d = trace?.document;
  if (!d) return null;
  if (d.error === true) return { estado: "fallo", nombre: d.name };
  const x = d.dropped;
  const partes = x
    ? [
        x.repeated > 0 ? `${x.repeated} ${x.repeated === 1 ? "repetido" : "repetidos"}` : null,
        x.figures > 0 ? `${x.figures} ${x.figures === 1 ? "cifra" : "cifras"}` : null,
        x.tooLong > 0 ? `${x.tooLong} demasiado ${x.tooLong === 1 ? "largo" : "largos"}` : null,
        x.overMax > 0 ? `${x.overMax} más allá del tope` : null,
      ].filter((p): p is string => p !== null)
    : [];
  return { estado: "leido", nombre: d.name, candidatos: d.candidates, correcciones: d.superseding, fuera: partes.length ? partes.join(", ") : null };
}

/** De dónde nació un criterio, como se lee en la memoria del inspector (el origen del hecho; `document` desde ADR-21 pt 6). */
export function nacioDe(origin: string | undefined): string {
  if (origin === "document") return "salió de un documento que adjuntó";
  if (origin === "signup") return "lo contó al dar de alta la casa";
  if (origin === "correction") return "corregido por su directivo/a";
  return "nació de una conversación";
}
